import { ProductsSortByEnum } from '@/types/product'
import { SortEnum } from '@/enums'
import { PAGINATION_DEFAULT_SIZE, PAGINATION_SIZE_OPTIONS } from '@/constants'
import type { CategoryType } from '@/types/category'
import { getLocalizedValue } from './utils'

type ParamsType = {
  applications?: string[]
  brands?: string[]
  models?: string[]
  q?: string
  page: number
  size: number
  sortBy: ProductsSortByEnum
  sort: SortEnum
  hasInvalidParams: boolean
  cleanedQueryString: string
}

type CategoryPathValidationResult = {
  isValid: boolean
  breadcrumbSlugs: string[] // Array of slugs in order for breadcrumb construction
}

/**
 * Validates a category URL path by checking parent slugs match with URL segments
 *
 * @param category - The category object with potential parents
 * @param urlSlugs - The slug segments from URL
 * @param locale - The backend locale for translation
 * @returns Validation result with breadcrumb slugs
 */
export const validateCategoryPath = (
  category: CategoryType,
  urlSlugs: string[],
  locale: string
): CategoryPathValidationResult => {
  const breadcrumbSlugs: string[] = []

  // Validate URL path by checking parent slugs match with URL segments
  if (category.parents && category.parents.length > 0) {
    // The expected number of parent slugs should match URL segments minus the last one
    if (urlSlugs.length - 1 !== category.parents.length) {
      return { isValid: false, breadcrumbSlugs: [] }
    }

    // Check each parent slug matches the corresponding URL segment
    for (let i = 0; i < category.parents.length; i++) {
      const parent = category.parents[i]
      const parentSlug = getLocalizedValue(parent, 'slug', locale)

      if (parentSlug !== urlSlugs[i]) {
        // Parent slug doesn't match URL segment - invalid URL
        return { isValid: false, breadcrumbSlugs: [] }
      }

      breadcrumbSlugs.push(parentSlug)
    }
  } else if (urlSlugs.length > 1) {
    // Category has no parents, but URL has multiple segments - invalid URL
    return { isValid: false, breadcrumbSlugs: [] }
  }

  // Add the current category slug
  const currentSlug = getLocalizedValue(category, 'slug', locale)
  if (currentSlug) {
    breadcrumbSlugs.push(currentSlug)
  }

  return { isValid: true, breadcrumbSlugs }
}

/**
 * Transform sortBy parameter to backend format with locale for translation fields
 *
 * @param sortBy - The sortBy enum value
 * @param locale - The backend locale (e.g., 'tr-TR', 'en-US')
 * @returns Transformed sortBy value for backend API
 * @example
 * transformSortByForBackend('title', 'tr-TR') // => 'translations.tr-TR.title'
 * transformSortByForBackend('price', 'tr-TR') // => 'price'
 */
export const transformSortByForBackend = (sortBy: ProductsSortByEnum, locale: string): string => {
  // If sortBy is TITLE, transform it to translations.{locale}.title format
  if (sortBy === ProductsSortByEnum.TITLE) {
    return `translations.${locale}.title`
  }

  // For other fields, return as-is
  return sortBy
}

/**
 * Validates and cleans URL search parameters for product listings
 *
 * @param searchParams - Raw search parameters from URL
 * @returns Validated parameters and cleaned query string
 */
export const validateProductParams = (searchParams: {
  applications?: string | string[]
  brands?: string | string[]
  models?: string | string[]
  q?: string
  page?: string
  size?: string
  sortBy?: string
  sort?: string
}): ParamsType => {
  // Helper function to parse comma-separated or array parameters
  const parseArrayParam = (param?: string | string[]): string[] | undefined => {
    if (!param) {
      return undefined
    }
    if (Array.isArray(param)) {
      return param
    }
    return param
      .split(',')
      .map(s => s.trim())
      .filter(Boolean)
  }

  // Parse filter parameters
  const applications = parseArrayParam(searchParams.applications)
  const brands = parseArrayParam(searchParams.brands)
  const models = parseArrayParam(searchParams.models)
  const q = searchParams.q || undefined

  // Validate page
  const page = Number(searchParams.page) || 1

  // Validate size
  const sizeParam = Number(searchParams.size)
  const size = PAGINATION_SIZE_OPTIONS.includes(sizeParam) ? sizeParam : PAGINATION_DEFAULT_SIZE

  // Validate sortBy
  const sortByParam = searchParams.sortBy as ProductsSortByEnum
  const validSortByValues = Object.values(ProductsSortByEnum)
  const sortBy = validSortByValues.includes(sortByParam) ? sortByParam : ProductsSortByEnum.CREATED_AT

  // Validate sort
  const sortParam = searchParams.sort as SortEnum
  const validSortValues = Object.values(SortEnum)
  const sort = validSortValues.includes(sortParam) ? sortParam : SortEnum.DESC

  // Check if any parameters are invalid
  const hasInvalidSortBy = searchParams.sortBy && !validSortByValues.includes(sortByParam)
  const hasInvalidSort = searchParams.sort && !validSortValues.includes(sortParam)
  const hasInvalidSize = searchParams.size && !PAGINATION_SIZE_OPTIONS.includes(sizeParam)
  const hasInvalidParams = !!(hasInvalidSortBy || hasInvalidSort || hasInvalidSize)

  // Build a cleaned query string with validated parameters
  let cleanedQueryString = ''
  if (hasInvalidParams) {
    const cleanParams = new URLSearchParams()

    // Add filter parameters
    if (applications?.length) {
      cleanParams.set('applications', applications.join(','))
    }
    if (brands?.length) {
      cleanParams.set('brands', brands.join(','))
    }
    if (models?.length) {
      cleanParams.set('models', models.join(','))
    }
    if (q) {
      cleanParams.set('q', q)
    }

    // Add page parameter if not default
    if (page > 1) {
      cleanParams.set('page', page.toString())
    }

    // Add size parameter if present (use validated value)
    if (searchParams.size) {
      cleanParams.set('size', size.toString())
    }

    // Add sortBy parameter if present (use validated value)
    if (searchParams.sortBy) {
      cleanParams.set('sortBy', sortBy)
    }

    // Add sort parameter if present (use validated value)
    if (searchParams.sort) {
      cleanParams.set('sort', sort)
    }

    cleanedQueryString = cleanParams.toString()
  }

  return {
    applications,
    brands,
    models,
    q,
    page,
    size,
    sortBy,
    sort,
    hasInvalidParams,
    cleanedQueryString
  }
}
